/**
 * ToastManager - Centralized toast notification system for the page editor
 *
 * Key features:
 * - Accessible: aria-live region for screen readers
 * - Queue management: max 3 visible toasts, older ones auto-dismissed
 * - XSS safe: uses textContent, never innerHTML for messages
 * - CSS class-based styling (not inline styles)
 *
 * Implementation: Phase 2 Task 1.1
 *
 * Usage:
 *   const toastManager = new ToastManager()
 *   toastManager.success('Changes saved!')
 *   toastManager.error('Save failed')
 *   toastManager.warning('Network issues detected')
 *   toastManager.info('Tip: Press ? for keyboard shortcuts')
 */

// Toast types with their icons (SVG paths)
const TOAST_ICONS = {
  success: 'M5 13l4 4L19 7',
  error: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
}

// Default configuration
const DEFAULT_OPTIONS = {
  maxVisible: 3,
  defaultDuration: 4000,
  position: 'bottom-right', // bottom-right, top-left, top-right, bottom-left
  containerClass: 'toast-container'
}

export class ToastManager {
  /**
   * @param {Object} options - Configuration options
   * @param {number} options.maxVisible - Max toasts visible at once (default 3)
   * @param {number} options.defaultDuration - Default auto-dismiss time in ms (default 4000)
   * @param {string} options.position - Container position (default 'bottom-right')
   */
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }
    this.container = null
    this.toasts = new Map() // id -> toast element
    this.toastCounter = 0
  }

  /**
   * Initialize the toast container (lazy, creates on first toast)
   * @private
   */
  ensureContainer() {
    if (this.container) return

    this.container = document.createElement('div')
    this.container.id = 'page-editor-toast-container'
    this.container.className = `toast-container toast-container--${this.options.position}`
    this.container.setAttribute('aria-live', 'polite')
    this.container.setAttribute('aria-atomic', 'false')
    this.container.setAttribute('role', 'status')
    document.body.appendChild(this.container)
  }

  /**
   * Show a toast notification
   *
   * @param {string} message - The message to display (plain text, will be escaped)
   * @param {string} type - Toast type: 'success', 'error', 'warning', 'info'
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @returns {string} Toast ID for manual dismissal
   */
  show(message, type = 'info', duration = this.options.defaultDuration) {
    this.ensureContainer()

    const id = `toast-${++this.toastCounter}`

    // Enforce max visible limit
    if (this.toasts.size >= this.options.maxVisible) {
      // Dismiss oldest toast
      const oldestId = this.toasts.keys().next().value
      this.dismiss(oldestId)
    }

    // Create toast element
    const toast = document.createElement('div')
    toast.id = id
    toast.className = `toast toast--${type}`
    toast.setAttribute('role', 'alert')
    // A11y: Use assertive for errors to interrupt screen reader, polite for others
    toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite')

    // Create icon (SVG)
    const iconSvg = this.createIcon(type)

    // Create message span (XSS safe: textContent)
    const messageSpan = document.createElement('span')
    messageSpan.className = 'toast__message'
    messageSpan.textContent = message

    // Create dismiss button
    const dismissBtn = document.createElement('button')
    dismissBtn.className = 'toast__dismiss'
    dismissBtn.setAttribute('aria-label', 'Dismiss')
    dismissBtn.innerHTML = '&times;'
    dismissBtn.onclick = () => this.dismiss(id)

    // Assemble toast
    toast.appendChild(iconSvg)
    toast.appendChild(messageSpan)
    toast.appendChild(dismissBtn)

    // Add to container
    this.container.appendChild(toast)
    this.toasts.set(id, toast)

    // Trigger enter animation
    requestAnimationFrame(() => {
      toast.classList.add('toast--visible')
    })

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration)
    }

    return id
  }

  /**
   * Create SVG icon element
   * @private
   */
  createIcon(type) {
    const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    iconSvg.setAttribute('class', 'toast__icon')
    iconSvg.setAttribute('width', '20')
    iconSvg.setAttribute('height', '20')
    iconSvg.setAttribute('fill', 'none')
    iconSvg.setAttribute('stroke', 'currentColor')
    iconSvg.setAttribute('viewBox', '0 0 24 24')

    const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    iconPath.setAttribute('stroke-linecap', 'round')
    iconPath.setAttribute('stroke-linejoin', 'round')
    iconPath.setAttribute('stroke-width', '2')
    iconPath.setAttribute('d', TOAST_ICONS[type] || TOAST_ICONS.info)
    iconSvg.appendChild(iconPath)

    return iconSvg
  }

  /**
   * Dismiss a toast by ID
   *
   * @param {string} id - Toast ID to dismiss
   */
  dismiss(id) {
    const toast = this.toasts.get(id)
    if (!toast) return

    // Trigger exit animation
    toast.classList.remove('toast--visible')
    toast.classList.add('toast--exiting')

    // Remove after animation
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast)
      }
      this.toasts.delete(id)
    }, 300) // Match CSS animation duration
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    for (const id of this.toasts.keys()) {
      this.dismiss(id)
    }
  }

  // Convenience methods
  success(message, duration) {
    return this.show(message, 'success', duration)
  }

  error(message, duration = 6000) {
    // Errors stay longer by default
    return this.show(message, 'error', duration)
  }

  warning(message, duration) {
    return this.show(message, 'warning', duration)
  }

  info(message, duration) {
    return this.show(message, 'info', duration)
  }

  /**
   * Destroy the toast manager and clean up
   */
  destroy() {
    this.dismissAll()
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
    }
    this.container = null
  }
}
